import { useRef } from "react";
import {
	type GoalFormModel,
	getDefaultDueDate,
	type UseGoalFormReturn,
} from "web_myplann/Goals/Forms/useGoalForm";
import { RentDrawdownMode } from "web_myplann/Goals/Types/RentDrawdownMode";
import type { GoalCategoryTypeEnum } from "web_myplann/Infrastructure/Api/Api";

const CATEGORY_FIELD_KEYS = [
	"name",
	"dueDate",
	"totalValue",
	"ownFunds",
	"loanAmount",
	"monthlyRent",
	"statePension",
	"rentYears",
	"drawdownMode",
] as const;

type CategoryFields = Pick<GoalFormModel, (typeof CATEGORY_FIELD_KEYS)[number]>;

export const useGoalCategoryFieldMemory = (form: UseGoalFormReturn) => {
	const memoryRef = useRef(new Map<GoalCategoryTypeEnum, CategoryFields>());

	const selectCategory = (next: GoalCategoryTypeEnum | undefined) => {
		const current = form.getValues();
		const prev = current.category;
		if (prev === next) return;

		if (prev) {
			memoryRef.current.set(
				prev,
				Object.fromEntries(
					CATEGORY_FIELD_KEYS.map((key) => [key, current[key]]),
				) as CategoryFields,
			);
		}

		const restored = next ? memoryRef.current.get(next) : undefined;
		const restoredFields = Object.fromEntries(
			CATEGORY_FIELD_KEYS.map((key) => [key, restored?.[key]]),
		) as Partial<CategoryFields>;

		form.reset(
			{
				...current,
				...restoredFields,
				category: next,
				name: restored?.name ?? "",
				dueDate: restored?.dueDate ?? getDefaultDueDate(),
				ownFunds: restored?.ownFunds ?? 0,
				loanAmount: restored?.loanAmount ?? 0,
				drawdownMode: restored?.drawdownMode ?? RentDrawdownMode.ToZero,
			},
			{ keepDefaultValues: true },
		);
	};

	return { selectCategory };
};
